import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, Mail, Lock, ArrowRight, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "@/lib/adminApi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_66258ad9-e000-456c-bbb1-9b3da697cdba/artifacts/pl46n86d_New%20SCAH%20Logo.png";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [adminExists, setAdminExists] = useState(true);
  const [isSetup, setIsSetup] = useState(false);
  
  // Login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  // Setup state
  const [setupData, setSetupData] = useState({
    email: "",
    password: "",
    name: "",
  });

  useEffect(() => {
    // Check if admin exists and if there's an active session
    const init = async () => {
      try {
        const response = await axios.get(`${API}/admin/check`);
        setAdminExists(response.data.admin_exists);
        if (!response.data.admin_exists) {
          setIsSetup(true);
        }
      } catch (error) {
        console.error("Error checking admin:", error);
      }
      // Check if already logged in via httpOnly cookie
      try {
        await axios.get(`${API}/admin/me`);
        navigate("/admin/dashboard");
      } catch {
        // Not authenticated — stay on login
      }
    };
    init();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/login`, { email, password });
      toast.success("Login successful!");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    if (setupData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);

    try {
      await axios.post(`${API}/admin/setup`, setupData);
      toast.success("Admin account created!");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Setup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="SCAH Logo" 
            className="h-20 w-auto mx-auto mb-4"
          />
          <h1 className="font-serif text-3xl font-bold text-white mb-2">
            Admin Portal
          </h1>
          <p className="text-slate-400">
            Sickle Cell Association
          </p>
        </div>

        <Card className="border-slate-800 bg-slate-800/50">
          <CardContent className="p-8">
            {isSetup && !adminExists ? (
              // Setup Form
              <form onSubmit={handleSetup}>
                <div className="flex items-center gap-2 mb-6 text-amber-400">
                  <UserPlus className="w-5 h-5" />
                  <span className="font-medium">Create Admin Account</span>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="setup-name" className="text-slate-300">
                      Your Name
                    </Label>
                    <Input
                      id="setup-name"
                      type="text"
                      placeholder="Admin Name"
                      value={setupData.name}
                      onChange={(e) => setSetupData({ ...setupData, name: e.target.value })}
                      className="mt-1 bg-slate-700 border-slate-600 text-white"
                      required
                      data-testid="setup-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="setup-email" className="text-slate-300">
                      Email Address
                    </Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="setup-email"
                        type="email"
                        placeholder="admin@sicklecellhouston.org"
                        value={setupData.email}
                        onChange={(e) => setSetupData({ ...setupData, email: e.target.value })}
                        className="pl-10 bg-slate-700 border-slate-600 text-white"
                        required
                        data-testid="setup-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="setup-password" className="text-slate-300">
                      Password
                    </Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="setup-password"
                        type="password"
                        placeholder="••••••••"
                        value={setupData.password}
                        onChange={(e) => setSetupData({ ...setupData, password: e.target.value })}
                        className="pl-10 bg-slate-700 border-slate-600 text-white"
                        required
                        data-testid="setup-password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 mt-4"
                    data-testid="setup-submit-btn"
                  >
                    {loading ? "Creating Account..." : "Create Admin Account"}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </form>
            ) : (
              // Login Form
              <form onSubmit={handleLogin}>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="login-email" className="text-slate-300">
                      Email Address
                    </Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="admin@sicklecellhouston.org"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 bg-slate-700 border-slate-600 text-white"
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="login-password" className="text-slate-300">
                      Password
                    </Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 bg-slate-700 border-slate-600 text-white"
                        required
                        data-testid="login-password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 mt-4"
                    data-testid="login-submit-btn"
                  >
                    {loading ? "Signing In..." : "Sign In to Admin"}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>

                  <div className="text-center pt-1">
                    <Link
                      to="/admin/forgot-password"
                      className="text-sm text-slate-400 hover:text-amber-300"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          <a href="/" className="hover:text-slate-300">← Back to Website</a>
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
